import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { fetchAuditLogs, getAuditLogStats } from '../../../services/auditLogService';
import { format } from 'date-fns';

const AuditLogViewer = ({ onClose }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    eventType: '',
    eventCategory: '',
    severity: '',
    dateFrom: '',
    dateTo: '',
    limit: 100
  });
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    loadAuditLogs();
    loadStats();
  }, [filters]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await fetchAuditLogs(filters);
      if (error) {
        console.error('Error loading audit logs:', error);
      } else {
        setLogs(data || []);
      }
    } catch (error) {
      console.error('Exception loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error } = await getAuditLogStats(filters);
      if (!error) {
        setStats(data);
      }
    } catch (error) {
      console.error('Exception loading stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      eventType: '',
      eventCategory: '',
      severity: '',
      dateFrom: '',
      dateTo: '',
      limit: 100
    });
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'info':
      default:
        return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'security':
        return 'Shield';
      case 'compliance':
        return 'FileCheck';
      case 'data_modification':
        return 'Edit';
      case 'system':
        return 'Settings';
      default:
        return 'Activity';
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl border border-border w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="FileText" size={24} />
              Sicherheits-Audit-Protokoll
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Vollständige Überwachung aller kritischen Operationen für Compliance und Sicherheit
            </p>
          </div>
          <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="p-6 border-b border-border bg-muted/30">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Gesamt-Einträge</p>
                <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Kritisch</p>
                <p className="text-2xl font-bold text-red-600">{stats?.bySeverity?.critical || 0}</p>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Warnungen</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.bySeverity?.warning || 0}</p>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Info</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.bySeverity?.info || 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="p-6 border-b border-border bg-muted/10">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <select
              value={filters?.eventCategory}
              onChange={(e) => handleFilterChange('eventCategory', e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="">Alle Kategorien</option>
              <option value="security">Sicherheit</option>
              <option value="compliance">Compliance</option>
              <option value="data_modification">Datenänderung</option>
              <option value="system">System</option>
            </select>

            <select
              value={filters?.severity}
              onChange={(e) => handleFilterChange('severity', e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="">Alle Schweregrade</option>
              <option value="info">Info</option>
              <option value="warning">Warnung</option>
              <option value="critical">Kritisch</option>
            </select>

            <input
              type="date"
              value={filters?.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Von Datum"
            />

            <input
              type="date"
              value={filters?.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Bis Datum"
            />

            <Button
              variant="outline"
              size="sm"
              iconName="RotateCcw"
              onClick={handleResetFilters}
            >
              Zurücksetzen
            </Button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : logs?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Keine Audit-Protokolle gefunden</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs?.map((log) => (
                <div
                  key={log?.id}
                  className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setExpandedLog(expandedLog === log?.id ? null : log?.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">
                        <Icon name={getCategoryIcon(log?.eventCategory)} size={20} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(log?.severity)}`}>
                            {log?.severity?.toUpperCase()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(log?.createdAt)}
                          </span>
                          {log?.userEmail && (
                            <span className="text-xs text-muted-foreground">
                              von {log?.userEmail}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-foreground mt-2">
                          {log?.actionDescription}
                        </p>
                        {log?.entityName && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Entität: {log?.entityType} - {log?.entityName}
                          </p>
                        )}
                      </div>
                    </div>
                    <Icon
                      name={expandedLog === log?.id ? 'ChevronUp' : 'ChevronDown'}
                      size={20}
                      className="text-muted-foreground flex-shrink-0"
                    />
                  </div>

                  {/* Expanded Details */}
                  {expandedLog === log?.id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Event-Typ:</p>
                          <p className="text-foreground font-medium">{log?.eventType}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Kategorie:</p>
                          <p className="text-foreground font-medium">{log?.eventCategory}</p>
                        </div>
                        {log?.dealershipName && (
                          <div>
                            <p className="text-muted-foreground">Partner:</p>
                            <p className="text-foreground font-medium">{log?.dealershipName}</p>
                          </div>
                        )}
                        {log?.applicationCustomerName && (
                          <div>
                            <p className="text-muted-foreground">Kunde:</p>
                            <p className="text-foreground font-medium">{log?.applicationCustomerName}</p>
                          </div>
                        )}
                      </div>

                      {log?.oldValues && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Alte Werte:</p>
                          <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                            {JSON.stringify(log?.oldValues, null, 2)}
                          </pre>
                        </div>
                      )}

                      {log?.newValues && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Neue Werte:</p>
                          <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                            {JSON.stringify(log?.newValues, null, 2)}
                          </pre>
                        </div>
                      )}

                      {log?.metadata && (
                        <div>
                          <p className="text-sm text-muted-foreground mb-1">Metadaten:</p>
                          <pre className="text-xs bg-muted p-3 rounded overflow-auto">
                            {JSON.stringify(log?.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Zeige {logs?.length} Einträge
          </p>
          <Button variant="default" onClick={onClose} iconName="">
            Schließen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogViewer;